import type { User } from '@chatbot/shared'
import { UserSchema } from '@chatbot/shared'
import type React from 'react'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { API_BASE_URL } from '../config'

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  token: string | null
  isLoading: boolean
}

interface AuthContextType extends AuthState {
  login: (token: string, user: User) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    isLoading: true,
  })

  // Check for existing token on mount - only once per app
  useEffect(() => {
    const checkExistingAuth = async () => {
      const token = localStorage.getItem('auth_token')

      if (token) {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/validate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ credential: token }),
          })

          if (response.ok) {
            const rawData = await response.json()
            const validationResult = UserSchema.safeParse(rawData.user)

            if (validationResult.success) {
              setAuthState({
                isAuthenticated: true,
                user: validationResult.data,
                token,
                isLoading: false,
              })
              return
            }
          }
          localStorage.removeItem('auth_token')
        } catch (error) {
          console.error('Token validation error:', error)
          localStorage.removeItem('auth_token')
        }
      }

      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        isLoading: false,
      })
    }

    checkExistingAuth()
  }, [])

  const login = useCallback((token: string, user: User) => {
    flushSync(() => {
      setAuthState({
        isAuthenticated: true,
        user,
        token,
        isLoading: false,
      })
    })
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token')
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      isLoading: false,
    })

    // Sign out from Google
    if (window.google) {
      window.google.accounts.id.disableAutoSelect()
    }
  }, [])

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
